/**
 * SubscriptionsBundlingPage
 *
 * Subscription management page for billing admins.
 * Shows subscription details and provides in-app subscription management.
 */

import { useEffect, useMemo, useState } from 'react';
import { AccountBalance, CalendarMonth, CheckCircle, CreditCard } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { useEventTracking } from '@services/statsig';

import { useGetFullSubscriptionDetailsQuery, useUpdateBillingIntervalMutation } from './store/api';
import { useBundleCatalog } from './store/hooks';
import { CancelSubscriptionSection, InvoicesSection, UpdatePaymentMethodModal } from './components';
import type { BundleWithStatus } from './types';
import { calculateDiscountAmount, getDiscountDurationText } from './utils';

// Helper function to format dates
const formatDateTime = (dateString: string | Date | number | null | undefined): string => {
  if (!dateString) {
    return 'N/A';
  }
  let date: Date;
  if (typeof dateString === 'number') {
    // Assume Unix timestamp in seconds, convert to milliseconds
    date = new Date(dateString * 1000);
  } else {
    date = new Date(dateString);
  }
  if (isNaN(date.getTime())) {
    return 'Invalid Date';
  }
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

export default function SubscriptionsBundlingPage() {
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showIntervalConfirm, setShowIntervalConfirm] = useState<'month' | 'year' | null>(null);
  const [intervalError, setIntervalError] = useState<string | null>(null);
  const [previewInterval, setPreviewInterval] = useState<'month' | 'year' | null>(null);

  const { subscriptionManagement } = useEventTracking();

  // Fetch consolidated subscription details (includes billing info, payment method, billing interval)
  const {
    data: subscriptionDetails,
    isLoading: isLoadingSubscription,
    isFetching: isFetchingSubscription,
    error: subscriptionError,
    refetch: refetchSubscription,
  } = useGetFullSubscriptionDetailsQuery();

  const [updateBillingInterval, { isLoading: isUpdatingInterval }] =
    useUpdateBillingIntervalMutation();

  const { allBundles, annualDiscountRange } = useBundleCatalog();

  // Memoize bundle lookup map for O(1) access
  const bundleMap = useMemo(() => {
    const map = new Map<string, BundleWithStatus>();
    allBundles.forEach(bundle => map.set(bundle.bundle_id, bundle));
    return map;
  }, [allBundles]);

  // Compute effective interval for display
  // Preview interval allows users to see pricing at different billing cycles
  // without changing their actual subscription. previewInterval is null when
  // not in preview mode, falling back to the subscription's actual interval.
  const displayInterval = previewInterval ?? subscriptionDetails?.billing_interval ?? 'month';
  const isPreviewMode =
    previewInterval !== null && previewInterval !== subscriptionDetails?.billing_interval;

  const handlePaymentMethodUpdate = () => {
    subscriptionManagement.paymentMethodUpdated();
    refetchSubscription();
  };

  const handleSubscriptionStatusChange = () => {
    refetchSubscription();
  };

  const handleIntervalChange = (
    _: React.MouseEvent<HTMLElement>,
    newInterval: 'month' | 'year' | null,
  ) => {
    if (newInterval) {
      // If selecting current interval, clear preview
      if (newInterval === subscriptionDetails?.billing_interval) {
        setPreviewInterval(null);
      } else {
        setPreviewInterval(newInterval);
        subscriptionManagement.billingIntervalPreview({
          preview_interval: newInterval,
          current_interval: subscriptionDetails?.billing_interval,
        });
      }
    }
  };

  const handleConfirmIntervalChange = async () => {
    if (!showIntervalConfirm) return;
    setIntervalError(null);
    try {
      await updateBillingInterval({
        changeBillingIntervalRequest: { billing_interval: showIntervalConfirm },
      }).unwrap();
      subscriptionManagement.billingIntervalConfirmed({
        from_interval: subscriptionDetails?.billing_interval,
        to_interval: showIntervalConfirm,
      });
      refetchSubscription();
      setShowIntervalConfirm(null);
      setPreviewInterval(null); // Reset preview
    } catch (err: any) {
      setIntervalError(err?.data?.message || 'Failed to change billing interval');
    }
  };

  const getStatusSeverity = (
    status: string | undefined | null,
  ): 'success' | 'info' | 'error' | 'warning' => {
    if (!status) return 'info';
    const statusLower = status.toLowerCase();
    if (statusLower === 'active') return 'success';
    if (statusLower === 'trialing') return 'info';
    if (statusLower === 'canceled' || statusLower === 'cancelled') return 'error';
    if (statusLower === 'past_due' || statusLower === 'paused' || statusLower === 'unpaid')
      return 'warning';
    return 'info';
  };

  /**
   * Determine if the current subscription status requires payment action.
   * This includes statuses where payment has failed, is missing, or subscription is paused.
   */
  const requiresPaymentAction = (status: string | undefined | null): boolean => {
    if (!status) return false;
    const statusLower = status.toLowerCase();
    return ['past_due', 'paused', 'unpaid', 'canceled', 'cancelled'].includes(statusLower);
  };

  /**
   * Get contextual button text based on subscription status
   */
  const getPaymentButtonText = (status: string | undefined | null): string => {
    if (!status) return 'Add payment method';
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'past_due':
        return 'Update payment';
      case 'paused':
        return 'Resume subscription';
      case 'unpaid':
        return 'Fix payment';
      case 'canceled':
      case 'cancelled':
        return 'Reactivate';
      default:
        return 'Add payment method';
    }
  };

  /**
   * Get button color based on alert severity
   */
  const getPaymentButtonColor = (
    severity: 'success' | 'info' | 'error' | 'warning',
  ): 'primary' | 'warning' | 'error' => {
    switch (severity) {
      case 'warning':
        return 'warning';
      case 'error':
        return 'error';
      default:
        return 'primary';
    }
  };

  // Check if user needs to add payment method
  const needsPaymentMethod =
    requiresPaymentAction(subscriptionDetails?.subscription_status) ||
    !subscriptionDetails?.payment_method;

  // Format currency amount
  const formatCurrency = (amount: number | null | undefined, currency: string = 'usd'): string => {
    if (!amount) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency.toUpperCase(),
    }).format(amount / 100); // Stripe amounts are in cents
  };

  // Get price for a line item - always use actual Stripe price from subscription
  // This ensures special pricing arrangements are displayed correctly
  const getItemPrice = (item: any): number => {
    const quantity = item.quantity || 1;

    // Always use actual subscription price (item.unit_amount comes from Stripe subscription item)
    // This correctly reflects any special/negotiated pricing arrangements
    const actualPrice = (item.unit_amount || 0) * quantity;

    // If actual billing is yearly, item.unit_amount is the yearly price - convert to monthly for display
    if (subscriptionDetails?.billing_interval === 'year') {
      return Math.round(actualPrice / 12);
    }

    return actualPrice;
  };

  // Track page view on mount
  useEffect(() => {
    if (!isLoadingSubscription && subscriptionDetails) {
      const discount = subscriptionDetails.discount;
      const hasDiscount = !!discount && (!!discount.percent_off || !!discount.amount_off);

      subscriptionManagement.pageViewed({
        subscription_status: subscriptionDetails.subscription_status,
        billing_interval: subscriptionDetails.billing_interval,
        has_payment_method: !!subscriptionDetails.payment_method,
        is_trialing: subscriptionDetails.is_trialing,
        has_discount: hasDiscount,
        discount_type: hasDiscount
          ? discount?.percent_off
            ? 'percent'
            : 'fixed'
          : undefined,
        discount_duration: hasDiscount ? (discount?.duration ?? undefined) : undefined,
      });

      if (discount && (discount.percent_off || discount.amount_off)) {
        subscriptionManagement.discountDisplayed({
          discount_type: discount.percent_off ? 'percent' : 'fixed',
          discount_duration: discount.duration ?? undefined,
          coupon_name: discount.coupon_name ?? undefined,
        });
      }
    }
  }, [isLoadingSubscription, subscriptionDetails, subscriptionManagement]);

  // Format payment method display
  const formatPaymentMethodDisplay = () => {
    if (!subscriptionDetails?.payment_method) {
      return { text: 'No payment method', icon: <CreditCard sx={{ color: 'primary.main' }} /> };
    }

    const pm = subscriptionDetails.payment_method;
    let card: any;
    let bankAccount: any;

    // Extract payment method details
    if ('payment_method' in pm && pm.payment_method) {
      const nested = pm.payment_method as any;
      card = nested.card;
      bankAccount = nested.us_bank_account;
    } else {
      card = (pm as any).card;
      bankAccount = (pm as any).us_bank_account;
    }

    if (card) {
      const brandMap: Record<string, string> = {
        visa: 'Visa',
        mastercard: 'Mastercard',
        amex: 'American Express',
        discover: 'Discover',
        diners: 'Diners Club',
        jcb: 'JCB',
        unionpay: 'UnionPay',
      };
      const brand = brandMap[card.brand?.toLowerCase()] || card.brand?.toUpperCase() || 'Card';
      const monthStr = card.exp_month?.toString().padStart(2, '0') || '';
      const yearStr = card.exp_year?.toString().slice(-2) || '';
      return {
        text: `${brand} ••••${card.last4 || ''} · Exp ${monthStr}/${yearStr}`,
        icon: <CreditCard sx={{ color: 'primary.main' }} />,
      };
    }

    if (bankAccount) {
      return {
        text: `${bankAccount.bank_name || 'Bank'} ••••${bankAccount.last4 || ''}`,
        icon: <AccountBalance sx={{ color: 'primary.main' }} />,
      };
    }

    return { text: 'No payment method', icon: <CreditCard sx={{ color: 'primary.main' }} /> };
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, minHeight: '100%' }}>
      {/* Subscription Details */}
      {isLoadingSubscription ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      ) : subscriptionError ? (
        <Alert severity="warning">
          Unable to load subscription details. Please try again later.
        </Alert>
      ) : subscriptionDetails && subscriptionDetails.subscription_status ? (
        <>
          {/* Status Banner - Full Width, Compact */}
          <Alert
            severity={getStatusSeverity(subscriptionDetails.subscription_status)}
            icon={subscriptionDetails.is_trialing ? <CalendarMonth /> : <CheckCircle />}
            action={
              needsPaymentMethod ? (
                <Button
                  variant="contained"
                  size="small"
                  color={getPaymentButtonColor(
                    getStatusSeverity(subscriptionDetails.subscription_status),
                  )}
                  onClick={() => {
                    subscriptionManagement.paymentMethodUpdateClicked();
                    setShowPaymentModal(true);
                  }}
                  sx={{
                    minWidth: 140,
                    fontWeight: 600,
                  }}
                >
                  {getPaymentButtonText(subscriptionDetails.subscription_status)}
                </Button>
              ) : undefined
            }
            sx={{
              borderRadius: 2,
              border: 1,
              borderColor: theme =>
                theme.palette[getStatusSeverity(subscriptionDetails.subscription_status)].main,
              '& .MuiAlert-action': {
                marginRight: 0,
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography component="span" sx={{ fontWeight: 600 }}>
                {subscriptionDetails.subscription_status
                  ? subscriptionDetails.subscription_status.charAt(0).toUpperCase() +
                    subscriptionDetails.subscription_status.slice(1)
                  : 'Unknown'}
              </Typography>
              {subscriptionDetails.is_trialing &&
                subscriptionDetails.trial_days_remaining !== null &&
                subscriptionDetails.trial_days_remaining !== undefined && (
                  <>
                    <Typography component="span">·</Typography>
                    <Typography component="span">
                      {subscriptionDetails.trial_days_remaining > 0
                        ? `${subscriptionDetails.trial_days_remaining} days remaining`
                        : 'Trial ended'}
                    </Typography>
                    <Typography component="span">·</Typography>
                    <Typography component="span">
                      Ends {formatDateTime(subscriptionDetails.trial_end)}
                    </Typography>
                  </>
                )}
            </Box>
          </Alert>

          {/* Plan & Payment Card - Consolidated */}
          <Card
            elevation={1}
            sx={{ borderRadius: 2, border: 1, borderColor: 'grey.200', position: 'relative' }}
          >
            {/* Loading Overlay - Shows during refetch after mutation */}
            {isFetchingSubscription && !isLoadingSubscription && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 1,
                  borderRadius: 2,
                }}
              >
                <CircularProgress />
              </Box>
            )}
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
                Your Plan
              </Typography>
              <Grid container spacing={4}>
                {/* Left Column: Line Items */}
                <Grid item xs={12} md={7}>
                  {subscriptionDetails.line_items && subscriptionDetails.line_items.length > 0 ? (
                    <>
                      {(() => {
                        const tierItems = subscriptionDetails.line_items.filter(
                          item => item.bundle_type === 'tier',
                        );
                        const addOnItems = subscriptionDetails.line_items.filter(
                          item => item.bundle_type !== 'tier',
                        );

                        return (
                          <>
                            {/* Tier bundles */}
                            {tierItems.map((item, index) => (
                              <Box
                                key={item.stripe_subscription_item_id || index}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  pb: 2,
                                }}
                              >
                                <Box sx={{ flex: 1, mr: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {item.bundle_name || 'Subscription'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Qty: {item.quantity || 1}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(getItemPrice(item), item.currency || 'usd')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    /mo
                                  </Typography>
                                </Box>
                              </Box>
                            ))}

                            {/* Add-ons section header */}
                            {addOnItems.length > 0 && (
                              <Typography
                                variant="overline"
                                color="text.secondary"
                                sx={{ display: 'block', mb: 1 }}
                              >
                                Add-ons
                              </Typography>
                            )}

                            {/* Add-on bundles */}
                            {addOnItems.map((item, index) => (
                              <Box
                                key={item.stripe_subscription_item_id || index}
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  pb: 2,
                                }}
                              >
                                <Box sx={{ flex: 1, mr: 2 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {item.bundle_name || 'Subscription'}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Qty: {item.quantity || 1}
                                  </Typography>
                                </Box>
                                <Box sx={{ textAlign: 'right' }}>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    {formatCurrency(getItemPrice(item), item.currency || 'usd')}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    /mo
                                  </Typography>
                                </Box>
                              </Box>
                            ))}
                          </>
                        );
                      })()}
                      {/* Total (with optional discount) */}
                      <Divider sx={{ mb: 2 }} />
                      {(() => {
                        const subtotal = subscriptionDetails.line_items.reduce(
                          (sum, item) => sum + getItemPrice(item),
                          0,
                        );
                        const currency = subscriptionDetails.line_items[0]?.currency || 'usd';
                        const discount = subscriptionDetails.discount;
                        const hasDiscount =
                          !!discount && (!!discount.percent_off || !!discount.amount_off);
                        const monthlyDiscountAmount = calculateDiscountAmount(
                          discount,
                          subtotal,
                          displayInterval,
                        );
                        const total = Math.max(0, subtotal - monthlyDiscountAmount);
                        const durationText = getDiscountDurationText(discount);

                        const discountLabel = hasDiscount
                          ? discount?.coupon_name ||
                            (discount?.percent_off
                              ? `${discount.percent_off}% off`
                              : `${formatCurrency(discount?.amount_off ?? 0, discount?.currency || currency)} off`)
                          : '';

                        // For annual "Billed $X annually", calculate the full annual discount
                        const annualTotal = hasDiscount
                          ? Math.max(
                              0,
                              subtotal * 12 -
                                (discount?.percent_off
                                  ? Math.round(
                                      (subtotal * 12 * discount.percent_off) / 100,
                                    )
                                  : discount?.amount_off || 0),
                            )
                          : subtotal * 12;

                        return (
                          <>
                            {/* Subtotal row - only when discount active */}
                            {hasDiscount && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  mb: 1,
                                }}
                              >
                                <Typography variant="body2" color="text.secondary">
                                  Subtotal
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  {formatCurrency(subtotal, currency)}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 0.5 }}
                                  >
                                    /mo
                                  </Typography>
                                </Typography>
                              </Box>
                            )}

                            {/* Discount row */}
                            {hasDiscount && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'flex-start',
                                  mb: 1,
                                }}
                              >
                                <Box>
                                  <Typography variant="body2" color="success.main">
                                    Discount: {discountLabel}
                                  </Typography>
                                  {durationText && (
                                    <Typography variant="caption" color="text.secondary">
                                      {durationText}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="body2" color="success.main">
                                  -{formatCurrency(monthlyDiscountAmount, currency)}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 0.5 }}
                                  >
                                    /mo
                                  </Typography>
                                </Typography>
                              </Box>
                            )}

                            {/* Total row */}
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'flex-start',
                              }}
                            >
                              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                Total
                              </Typography>
                              <Box sx={{ textAlign: 'right' }}>
                                <Typography variant="body1" sx={{ fontWeight: 700 }}>
                                  {formatCurrency(total, currency)}
                                  <Typography
                                    component="span"
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 0.5 }}
                                  >
                                    /mo
                                  </Typography>
                                </Typography>
                                {displayInterval === 'year' && (
                                  <Typography variant="caption" color="text.secondary">
                                    Billed {formatCurrency(annualTotal, currency)} annually
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          </>
                        );
                      })()}
                    </>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No plan items
                    </Typography>
                  )}
                </Grid>

                {/* Right Column: Billing Settings */}
                <Grid item xs={12} md={5}>
                  <Box
                    sx={{
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                      p: 3,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 4,
                    }}
                  >
                    {/* Billing Cycle */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Billing Cycle
                      </Typography>
                      {(() => {
                        // Calculate discount label
                        const discountLabel = annualDiscountRange
                          ? annualDiscountRange.min === annualDiscountRange.max
                            ? `-${annualDiscountRange.min}%`
                            : `-${annualDiscountRange.min}-${annualDiscountRange.max}%`
                          : null;

                        return (
                          <ToggleButtonGroup
                            value={displayInterval}
                            exclusive
                            onChange={handleIntervalChange}
                            size="small"
                            disabled={isUpdatingInterval}
                            fullWidth
                            sx={{
                              bgcolor: 'rgba(230, 225, 255, 0.2)',
                              border: '1px solid rgba(0, 13, 77, 0.12)',
                              borderRadius: '6px',
                              '& .MuiToggleButton-root': {
                                color: 'rgba(83, 89, 118, 0.7)',
                                border: 'none',
                                '&.Mui-selected': {
                                  bgcolor: '#ffffff',
                                  color: '#535976',
                                  border: '1px solid rgba(0, 13, 77, 0.45)',
                                  boxShadow:
                                    '0 2px 4px -2px rgba(0, 0, 0, 0.08), 0 4px 8px -2px rgba(0, 0, 0, 0.04)',
                                  '&:hover': {
                                    bgcolor: '#ffffff',
                                  },
                                },
                                '&:hover': {
                                  bgcolor: 'transparent',
                                  color: 'rgba(83, 89, 118, 0.9)',
                                },
                              },
                            }}
                          >
                            <ToggleButton value="month" sx={{ px: 2 }}>
                              Monthly
                            </ToggleButton>
                            <ToggleButton value="year" sx={{ px: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                                <span>Annual</span>
                                {discountLabel && (
                                  <Box
                                    component="span"
                                    sx={theme => ({
                                      bgcolor: alpha(theme.palette.primary.main, 0.1),
                                      color: 'primary.main',
                                      borderRadius: '10px',
                                      px: 0.75,
                                      py: 0.25,
                                      fontSize: '0.65rem',
                                      fontWeight: 700,
                                      lineHeight: 1,
                                    })}
                                  >
                                    {discountLabel}
                                  </Box>
                                )}
                              </Box>
                            </ToggleButton>
                          </ToggleButtonGroup>
                        );
                      })()}
                      {isPreviewMode && (
                        <Button
                          variant="contained"
                          size="small"
                          fullWidth
                          onClick={() => {
                            setShowIntervalConfirm(previewInterval);
                            setIntervalError(null);
                          }}
                          sx={{ mt: 1.5 }}
                        >
                          Apply {previewInterval === 'year' ? 'Annual' : 'Monthly'} Billing
                        </Button>
                      )}
                    </Box>

                    {/* Payment Method */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1.5 }}>
                        Payment Method
                      </Typography>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 2,
                        }}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                          {formatPaymentMethodDisplay().icon}
                          <Typography variant="body2" color="text.secondary">
                            {formatPaymentMethodDisplay().text}
                          </Typography>
                        </Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            subscriptionManagement.paymentMethodUpdateClicked();
                            setShowPaymentModal(true);
                          }}
                          sx={{ flexShrink: 0 }}
                        >
                          {subscriptionDetails.payment_method ? 'Update' : 'Add'}
                        </Button>
                      </Box>
                    </Box>

                    {/* Next Charge */}
                    {subscriptionDetails.current_period_end && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
                          Next charge
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {formatDateTime(subscriptionDetails.current_period_end)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      ) : subscriptionDetails && !subscriptionDetails.subscription_status ? (
        <Card elevation={2} sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No Active Subscription
          </Typography>
          <Typography variant="body2" color="text.secondary">
            You don&apos;t have an active subscription at this time.
          </Typography>
        </Card>
      ) : null}

      {/* Billing Information Section */}
      {subscriptionDetails?.subscription_status && (
        <>
          <InvoicesSection />

          {/* Cancel Subscription Section */}
          <CancelSubscriptionSection
            cancelAtPeriodEnd={subscriptionDetails.cancel_at_period_end ?? undefined}
            currentPeriodEnd={subscriptionDetails.current_period_end}
            subscriptionStatus={subscriptionDetails.subscription_status}
            onStatusChange={handleSubscriptionStatusChange}
          />
        </>
      )}

      {/* Update Payment Method & Billing Modal */}
      <UpdatePaymentMethodModal
        open={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        onSuccess={handlePaymentMethodUpdate}
        existingBillingDetails={subscriptionDetails?.billing_details}
      />

      {/* Billing Interval Change Confirmation Dialog */}
      <Dialog
        open={showIntervalConfirm !== null}
        onClose={() => {
          setShowIntervalConfirm(null);
          setPreviewInterval(null); // Reset preview
        }}
        maxWidth="sm"
      >
        <DialogTitle>Change Billing Cycle</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to switch to{' '}
            <strong>{showIntervalConfirm === 'year' ? 'annual' : 'monthly'}</strong> billing? This
            will update the billing cycle for all items in your subscription.
          </DialogContentText>
          {intervalError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {intervalError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setShowIntervalConfirm(null)} disabled={isUpdatingInterval}>
            Cancel
          </Button>
          <Button
            onClick={handleConfirmIntervalChange}
            variant="contained"
            disabled={isUpdatingInterval}
            startIcon={
              isUpdatingInterval ? <CircularProgress size={16} color="inherit" /> : undefined
            }
          >
            {isUpdatingInterval ? 'Updating...' : 'Confirm Change'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
